import * as React from 'react';
import { useSelector } from 'react-redux';
import { HugeiconsIcon } from '@hugeicons/react';
import { DashboardSquare01Icon, ServerStack01Icon } from '@hugeicons/core-free-icons';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { IS_LOCALHOST, LOCAL_ENV, LOCAL_ENVS, switchLocalEnvironment } from '@/config/Config';
import { PLATFORMS, switchLocalPlatform } from '@/constants/platform/platformConfig';
import { selectPlatform } from '@/redux/slices/platform/platformSlice';

const ENV_META = {
  dev: { label: 'Dev', dot: 'bg-success' },
  staging: { label: 'Staging', dot: 'bg-warning' },
  production: { label: 'Production', dot: 'bg-destructive' },
};

const PLATFORM_LABELS = {
  [PLATFORMS.EVENTS]: 'Events',
  [PLATFORMS.MEDIA]: 'Media',
};

/**
 * Localhost only: one menu for the dev-time switches, the API environment and the platform.
 * Both choices are remembered and apply with a reload. Each env keeps its own login, so
 * switching env lands signed in to that env (or at Log in).
 * `header` sits in the app header; `floating` covers pages without one, such as Log in.
 */
export default function LocalDevMenu({ placement = 'floating' }) {
  const platform = useSelector(selectPlatform);

  if (!IS_LOCALHOST) return null;

  const env = ENV_META[LOCAL_ENV];
  const inHeader = placement === 'header';
  const summary = `${env.label} · ${PLATFORM_LABELS[platform]}`;

  const menu = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant={inHeader ? 'ghost' : 'outline'}
          size="sm"
          aria-label={`Dev settings: ${summary}`}
          className={cn(
            inHeader
              ? 'text-muted-foreground hover:text-foreground'
              : 'rounded-full bg-background/85 shadow-sm backdrop-blur-xl'
          )}
        >
          <span className={cn('size-2 rounded-full', env.dot)} aria-hidden="true" />
          {/* Phones keep the header for the page name, so the dot stands alone there. */}
          <span className={cn(inHeader && 'max-sm:sr-only')}>{summary}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side={inHeader ? 'bottom' : 'top'} className="w-52">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="flex items-center gap-2">
            <HugeiconsIcon icon={ServerStack01Icon} strokeWidth={2} className="size-3.5" />
            API environment
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup value={LOCAL_ENV} onValueChange={switchLocalEnvironment}>
            {LOCAL_ENVS.map(key => (
              <DropdownMenuRadioItem key={key} value={key}>
                <span className={cn('size-2 rounded-full', ENV_META[key].dot)} aria-hidden="true" />
                {ENV_META[key].label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuLabel className="flex items-center gap-2">
            <HugeiconsIcon icon={DashboardSquare01Icon} strokeWidth={2} className="size-3.5" />
            Platform
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup value={platform} onValueChange={switchLocalPlatform}>
            {Object.entries(PLATFORM_LABELS).map(([key, label]) => (
              <DropdownMenuRadioItem key={key} value={key}>
                {label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (inHeader) return menu;

  // Hidden whenever the app header is on screen, since the header carries its own menu.
  return (
    <div className="fixed bottom-4 end-4 z-30 [body:has([data-slot=app-header])_&]:hidden">
      {menu}
    </div>
  );
}
